import React, { useState, useEffect, useRef } from 'react';
import StatusBar from '../components/StatusBar.js';
import MinimalistButton from "../components/buttons/MinimalistButton";
import './LabelingPage.css';
import { saveToLocalStorage, loadFromLocalStorage } from '../utils/StorageUtils';
import { base64ToImage, imageToBase64 } from '../utils/VerbatimUtils.js';
import { useSetup } from '../context/SetupContext';
import { useVerbatim } from '../context/VerbatimContext';
import { extractDomainInfo } from '../utils/URLUtils';

const BatchLabelingPage = () => {
    const { labels, labelTypes, addLabel, setLabels } = useSetup();
    const { setVerbatims, deleteVerbatim } = useVerbatim();
    const [verbatim, setVerbatim] = useState('');
    const [link, setLink] = useState('');
    const [linkError, setLinkError] = useState('');
    const [activeLabels, setActiveLabels] = useState({});
    const [suggestedLabels, setSuggestedLabels] = useState([]);
    const [tabs, setTabs] = useState([]);
    const [activeTab, setActiveTab] = useState('');
    const [lastID, setLastID] = useState(0);
    const [editingVerbatimId, setEditingVerbatimId] = useState(null);
    const { verbatimsToEdit, setVerbatimsToEdit } = useVerbatim(); // Destructure from context
    const [currentVerbatimIndex, setCurrentVerbatimIndex] = useState(0); // Track the current verbatim index
    const [sourceOptions, setSourceOptions] = useState(labels['Sources']?.map(label => label.name) || ["Blog", "Forum", "Review"]);
    const genderOptions = ['Woman', 'Man', 'Unknown Gender'];
    const ageOptions = ['<25', '25-34', '35-44', '45-54', '55+', 'Unknown Age'];
    const sentiments = ['positive', 'negative', 'mixed', 'neutral'];
    const [statusMessage, setStatusMessage] = useState('');
    const imageInputRef = useRef(null);
    const selectedRef = useRef(null);

    useEffect(() => {
        // Update the labeling tabs when either `labelTypes` or `activeTab` changes
        updateLabelingTabs();  // Call the function to update the tabs based on current state
    }, [labelTypes, activeTab]);  // Dependency array to ensure it runs when these variables change

    useEffect(() => {
        // Chargement des verbatims depuis IndexedDB au montage du composant
        const loadStoredVerbatims = async () => {
            try {
                // Charger les verbatims stockés
                let storedVerbatims = await loadFromLocalStorage('storedVerbatims');
                
                // Vérifier que les données chargées sont un tableau
                if (!Array.isArray(storedVerbatims)) {
                    console.warn('Les verbatims chargés ne sont pas un tableau, initialisation avec un tableau vide');
                    storedVerbatims = [];
                    return;
                }
                
                // Trouver l'ID le plus élevé et le définir comme dernier ID utilisé
                if (storedVerbatims.length > 0) {
                    const highestID = Math.max(...storedVerbatims.map(v => v.id));
                    setLastID(highestID);  // Définir l'ID le plus élevé comme dernier ID
                }
            } catch (error) {
                console.error('Erreur lors du chargement des verbatims:', error);
            }
        };
        
        loadStoredVerbatims();
    }, []);  // Tableau de dépendances vide, s'exécute une seule fois au montage du composant

    useEffect(() => {
        const loadVerbatim = async () => {
            if (verbatimsToEdit.length > 0 && currentVerbatimIndex < verbatimsToEdit.length) {
                try {
                    const verbatimId = verbatimsToEdit[currentVerbatimIndex]; // Obtenir l'ID du verbatim actuel
                    let storedVerbatims = await loadFromLocalStorage('storedVerbatims');
                    
                    // S'assurer que storedVerbatims est un tableau
                    if (!Array.isArray(storedVerbatims)) {
                        console.warn('storedVerbatims n\'est pas un tableau dans useEffect, initialisation avec un tableau vide');
                        storedVerbatims = [];
                        return; // Sortir si pas de verbatims
                    }
                    
                    const verbatimToLoad = storedVerbatims.find(v => v.id === verbatimId); // Trouver le verbatim par ID
                    
                    if (verbatimToLoad) {
                        setVerbatim(verbatimToLoad.verbatim);  // Définir le texte du verbatim à éditer
                        setLink(verbatimToLoad.link);  // Définir le lien à éditer
                        setActiveLabels(verbatimToLoad.labels || {});  // Définir les étiquettes du verbatim
                        setEditingVerbatimId(verbatimToLoad.id);  // Garder une trace du verbatim en cours d'édition
                    } else {
                        console.warn(`Verbatim avec ID ${verbatimId} non trouvé`);
                    }
                } catch (error) {
                    console.error('Erreur lors du chargement du verbatim:', error);
                    alert(`Erreur lors du chargement: ${error.message}`);
                }
            }
        };
        
        loadVerbatim();
    }, [currentVerbatimIndex, verbatimsToEdit]); // Dépendances incluent l'index actuel et verbatimsToEdit

      useEffect(() => {
        const selectedSource = Array.isArray(activeLabels?.["Sources"])
            ? activeLabels["Sources"][0]?.name
            : null;
    
        if (selectedRef.current && selectedSource) {
            selectedRef.current.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
                inline: "nearest"
            });
        }
      }, [activeLabels]);

    useEffect(() => {
        // Update the labels displayed on the page based on current suggestions and active tab
        updateLabelingPageLabels();  // Call the function to refresh the labels on the page
    }, [suggestedLabels, activeTab, activeLabels]);  // Dependencies: suggested labels, active tab, and active labels

    useEffect(() => {
        // Refresh the list of selected labels whenever `activeLabels` changes
        updateSelectedLabelsList();  // Call the function to update the list of selected labels
    }, [activeLabels]);  // Dependency array with `activeLabels`

    useEffect(() => {
        // Automatically suggest labels based on the content of the verbatim text
        if (verbatim && verbatim.length > 0) {
            const allText = Array.isArray(verbatim)
                ? verbatim
                    .filter(item => item.type === 'text')
                    .map(item => item.content)
                    .join(' ')
                : '';
            suggestLabelsFromVerbatim(allText);  // Call function to suggest labels
        }
    }, [verbatim, labels]);  // Dependencies: `verbatim` content and available `labels`


    /************************************************
     * STATE AND UI UPDATE FUNCTIONS 
     ************************************************/

    // Function to update the available labeling tabs
    const updateLabelingTabs = () => {
        // Filter out specific labels ('Gender', 'Age', 'Sentiment') from the labelTypes and set the available tabs
        const availableTabs = Object.keys(labelTypes).filter(type => !['Gender', 'Age', 'Sentiment', 'Sources'].includes(type));

        setTabs(availableTabs);  // Update the state to reflect the available tabs

        // Set a default tab if none is active and there are available tabs
        if (availableTabs.length > 0 && !activeTab) {
            setActiveTab(availableTabs[0]);  // Set the first available tab as the default active tab
        }
    };

    // Function to update the list of selected labels displayed on the page
    const updateSelectedLabelsList = () => {
        const list = document.getElementById('selected-labels-list'); // Get the container for selected labels
        if (!list) return;
        list.innerHTML = ''; // Clear any existing content

        // Loop through active labels and render them in the UI
        Object.entries(activeLabels ?? {}).forEach(([labelType, labelsArray]) => {
            const labels = Array.isArray(labelsArray) ? labelsArray : labelsArray ? [labelsArray] : []; // Ensure labelsArray is an array

            // Iterate through each label object within the current label type
            labels.forEach((labelObj) => {
                if (labelObj && labelObj.name && labelObj.state) { // Ensure the label object has valid name and state
                    const { name, state } = labelObj; // Destructure the name and state from the label object
                    const div = document.createElement('div'); // Create a new div for displaying the label

                    // Handle Gender label type with specific class based on name
                    if (labelType === 'Gender') {
                        div.textContent = `Gender: ${name}`; // Display "Gender: Man" or "Gender: Woman"
                        div.className = `gender-label gender-${name.toLowerCase().replace(/\s+/g, '-')}`; // Apply specific CSS class
                    }
                    // Handle Age label type with specific class based on name
                    else if (labelType === 'Age') {
                        div.textContent = `Age: ${name}`; // Display "Age: 25-34"
                        div.className = `age-label age-${name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`; // Apply specific CSS class
                    }
                    // Handle Sentiment label type with specific class based on state
                    else if (labelType === 'Sentiment') {
                        div.textContent = `Sentiment: ${state}`; // Display "Sentiment: Positive"
                        div.className = `sentiment-label ${state.toLowerCase()}`; // Apply sentiment-specific class
                    }
                    else if (labelType === 'Sources') {
                        let sourceName = ["Blog", "Forum", "Review"].includes(name) ? name.toLowerCase().replace(/[^a-z0-9]/g, '-') : 'social-media';
                        div.textContent = `Sources: ${name}`; // Display "Sentiment: Positive"
                        div.className = `source-label source-${sourceName.toLowerCase()}`; // Apply sentiment-specific class
                    }
                    // Handle other label types (non-Gender, non-Age, non-Sentiment)
                    else {
                        // For labels with sentiment enabled, show name and state, e.g., "LabelName (Positive)"
                        if (labelTypes[labelType]?.sentimentEnabled) {
                            div.textContent = `${name} (${state})`;
                            const stateClass = state ? `${state.toLowerCase().replace(/\s+/g, '-')}` : ''; // Create class based on sentiment state
                            div.className = `label-tag ${stateClass}`; // Apply class based on sentiment state (e.g., 'positive', 'negative')
                        } else {
                            div.textContent = name; // For non-sentiment labels, just display the label name
                            div.className = 'other-label-tag active'; // Apply a generic active label class
                        }
                    }

                    list.appendChild(div); // Add the newly created div to the selected labels list
                } else {
                    console.warn(`Skipping invalid label object:`, labelObj); // Log a warning for any invalid label objects
                }
            });
        });
    };

    // Function to update the button's visual state (e.g., 'positive', 'negative', etc.)
    const updateLabelButtonState = (button, state) => {
        const states = ['positive', 'negative', 'mixed', 'active', 'inactive'];  // Define all possible states
        states.forEach(s => button.classList.remove(s));  // Remove any existing state class from the button
        button.classList.add(state);  // Add the new state class to the button
    };

    // Function to update the labels displayed on the page based on the active tab and suggested labels
    const updateLabelingPageLabels = () => {
        const container = document.getElementById('label-labels');  // Get the container for displaying labels
        if (!container)  return;  // Exit early if the container is not found
        container.innerHTML = '';  // Clear any existing content

        // Ensure there is an active tab before proceeding
        if (!activeTab) {
            return;  // Exit early if no active tab is set
        }

        const relevantLabels = new Set();  // Use a set to store relevant labels without duplicates

        // Add active labels for the current active tab to relevantLabels
        Object.entries(activeLabels)
            .filter(([labelType, labelsArray]) => labelType === activeTab && Array.isArray(labelsArray))  // Ensure labelsArray is an array
            .forEach(([_, labelsArray]) => {
                labelsArray.forEach((labelObj) => {
                    relevantLabels.add({ name: labelObj.name, type: activeTab });  // Add each active label to relevant labels
                });
            });

        // Add suggested labels to relevantLabels if they match the active tab
        suggestedLabels
            .filter((lbl) => lbl.type === activeTab)  // Filter suggested labels to match the current active tab
            .forEach((lbl) => {
                relevantLabels.add(lbl);  // Add each suggested label to the relevant labels set
            });

        // Display the relevant labels under the current active tab
        relevantLabels.forEach(({ name, type }) => {
            if (type === activeTab) {
                // Prevent duplicate buttons for the same label
                const existingButton = Array.from(container.children).find(button => button.textContent === name);
                if (!existingButton) {
                    const button = document.createElement('button');  // Create a new button for the label
                    button.textContent = name;  // Set the button's text to the label name
                    button.className = 'label-button';  // Apply the appropriate CSS class for the label button
                    button.setAttribute('data-name', name);  // Add the label's name as a data attribute
                    button.setAttribute('data-type', type);  // Add the label's type as a data attribute
                    button.onclick = () => handleLabelClick(name, type);  // Add an onclick handler for the button

                    // Find the label's state by searching the activeLabels for the current tab
                    const labelGroup = activeLabels[activeTab] || [];  // Get the labels array for the active tab
                    const labelObj = labelGroup.find(l => l.name === name);  // Find the label object by name
                    const state = labelObj?.state || 'inactive';  // Get the label's state, default to 'inactive'

                    console.log("This is active labels", activeLabels);  // Log active labels for debugging
                    console.log("updateLabelButton state has this value", state);  // Log the label's state for debugging
                    updateLabelButtonState(button, state);  // Update the button's visual state based on the label's state

                    container.appendChild(button);  // Add the newly created button to the container
                }
            } else {
                console.log(`Label not displayed (type mismatch or already exists): ${name}`);  // Log if a label isn't displayed
            }
        });

        setupManualLabelInput();  // Set up the input for manually adding new labels
    };

    // Function to set up manual label input handling for manually adding new labels
    const setupManualLabelInput = () => {
        const input = document.getElementById('manual-label-input');  // Get the manual label input field
        const suggestionsContainer = document.getElementById('label-suggestions');  // Get the container for label suggestions

        // Add an event listener for when the user types in the input field
        input.addEventListener('input', function () {
            const query = input.value.toLowerCase();  // Get the input value and convert it to lowercase
            suggestionsContainer.innerHTML = '';  // Clear any previous suggestions

            if (query.length > 0) {
                // Get the labels for the active tab (i.e., the current label type)
                const labelsForTab = labels[activeTab] || [];

                // Filter the labels based on the query and exclude predefined types like Gender, Age, Sentiment aand Sources
                const filteredLabels = labelsForTab
                    .filter(labelObj =>
                        labelObj.name.toLowerCase().includes(query) &&
                        !['Gender', 'Age', 'Sentiment', 'Sources'].includes(labelObj.type)  // Exclude these label types from suggestions
                    )
                    .map(labelObj => labelObj.name);  // Get only the label names

                // Create suggestion elements for each filtered label
                filteredLabels.forEach((label) => {
                    const suggestionDiv = document.createElement('div');  // Create a div for the suggestion
                    suggestionDiv.textContent = label;  // Set the suggestion text to the label name
                    suggestionDiv.onclick = () => {
                        addManualLabel(label);  // Add the selected label when clicked
                        input.value = '';  // Clear the input field
                        suggestionsContainer.innerHTML = '';  // Clear the suggestions
                    };
                    suggestionsContainer.appendChild(suggestionDiv);  // Append the suggestion to the suggestions container
                });
            }
        });

        // Add an event listener for when the user presses the "Enter" key
        input.addEventListener('keypress', function (e) {
            if (e.key === 'Enter') {
                const query = input.value.trim();  // Trim whitespace from the input value
                if (query.length > 0) {
                    // Get the labels for the active tab (i.e., the current label type)
                    const labelsForTab = labels[activeTab] || [];

                    // Filter out predefined types like Gender, Age, and Sentiment and get valid label names
                    const validLabels = labelsForTab
                        .filter(labelObj =>
                            !['Gender', 'Age', 'Sentiment', 'Sources'].includes(labelObj.type)  // Exclude these label types
                        )
                        .map(labelObj => labelObj.name);  // Get only the label names

                    // Add the label if it's valid (exists in the label list for the current tab)
                    if (validLabels.includes(query)) {
                        addManualLabel(query);  // Add the manually input label
                        input.value = '';  // Clear the input field
                        suggestionsContainer.innerHTML = '';  // Clear the suggestions
                    }
                }
            }
        });
    };

    const storeVerbatim = async () => {
        // Fonction pour s'assurer que les étiquettes sont dans des tableaux
        const ensureLabelsAreArrays = (labels) => {
            const result = {};
            for (const labelType in labels) {
                if (labels[labelType] !== undefined && labels[labelType] !== null) {
                    result[labelType] = Array.isArray(labels[labelType]) ? labels[labelType] : [labels[labelType]];
                }
            }
            return result;
        };

        // Vérifier que les étiquettes obligatoires sont présentes
        const checkMandatoryLabels = () => {
            for (const labelType in labelTypes) {
                if (labelTypes[labelType]?.mandatory) {
                    const labelsArray = activeLabels[labelType];
                    if (!labelsArray || labelsArray.length === 0) {
                        alert(`Please select at least one label for the mandatory label type: ${labelType}`);
                        return false;
                    }
                }
            }
            return true;
        };

        // Vérifier que les étiquettes à valeur unique n'ont qu'une seule valeur
        const checkMultiValueLabels = () => {
            for (const labelType in labelTypes) {
                if (labelTypes[labelType]?.multiValue === false) {
                    const labelsArray = activeLabels[labelType];
                    if (Array.isArray(labelsArray) && labelsArray.length > 1) {
                        alert(`You can only select one label for the label type: ${labelType}`);
                        return false;
                    }
                }
            }
            return true;
        };

        try {
            // Le reste de la fonction storeVerbatim (le corps existant)
            if (verbatim && link && checkMandatoryLabels() && checkMultiValueLabels()) {
                // Déterminer l'ID du nouveau verbatim
                const newID = editingVerbatimId ?? lastID + 1;
                console.log("New ID for the verbatim:", newID);  // Log the new ID for debugging
                // Créer le nouvel objet verbatim
                const newVerbatim = {
                    id: newID,
                    verbatim,
                    link,
                    labels: ensureLabelsAreArrays(activeLabels),
                };

                // Récupérer les verbatims existants et s'assurer qu'ils sont dans un tableau
                let storedVerbatims = await loadFromLocalStorage('storedVerbatims');
                if (!Array.isArray(storedVerbatims)) {
                    console.warn('storedVerbatims n\'est pas un tableau, initialisation avec un tableau vide');
                    storedVerbatims = [];
                }

                const { hostname, hostnameWithoutTld, baseName, baseNameFormatted } = extractDomainInfo(link?.trim());

                if (
                    hostname && hostnameWithoutTld && baseName && baseNameFormatted &&
                    newVerbatim.link != storedVerbatims.find(v => v.id === newID).link && 
                    labels['Sources'] && 
                    !labels['Sources'].some(source => source.name?.toLowerCase() === baseName) &&
                    !labels['Sources'].some(source => source.keywords.includes(hostnameWithoutTld))
                ) {
                    // if the hostname is not already in the Sources label, add it
                    setLabels((prevLabels) => ({
                        ...prevLabels,
                        Sources: [...(prevLabels['Sources'] || []), { name: baseNameFormatted, keywords: [hostnameWithoutTld], explanation: '' }],
                    }));
                }

                let updatedVerbatims;
                if (editingVerbatimId !== null) {
                    // En mode édition, remplacer le verbatim existant
                    updatedVerbatims = storedVerbatims.map(v => v.id === newID ? newVerbatim : v);
                } else {
                    // En mode ajout, ajouter le nouveau verbatim
                    updatedVerbatims = [...storedVerbatims, newVerbatim];
                    setLastID(newID);
                }

                // Sauvegarder les verbatims mis à jour
                await saveToLocalStorage('storedVerbatims', updatedVerbatims);
                setVerbatims(updatedVerbatims); // Mettre à jour le contexte avec les verbatims
                

                // Réinitialiser les champs de formulaire
                setVerbatim('');
                setLink('');
                setLinkError('');
                setActiveLabels({});
                setSuggestedLabels([]);
                setEditingVerbatimId(null);

                // Gérer la navigation batch
                if (currentVerbatimIndex + 1 < verbatimsToEdit.length) {
                    setCurrentVerbatimIndex(currentVerbatimIndex + 1); // Passer au verbatim suivant
                } else {
                    // Si tous les verbatims ont été édités, terminer le processus
                    alert('Batch editing completed!');
                    setVerbatimsToEdit([]); // Vider la liste
                    setCurrentVerbatimIndex(0); // Réinitialiser l'index
                }

                setStatusMessage('Verbatim successfully stored!');
            } else {
                alert('Please fill in all required fields.');
            }
        } catch (error) {
            console.error('Erreur lors de la sauvegarde du verbatim:', error);
            alert(`Erreur lors de la sauvegarde: ${error.message}`);
        }
    };


    /************************************************
   * EVENT HANDLERS 
   ************************************************/

    // Handle changes to the verbatim input field
    const handleVerbatimChange = (event) => {
        if (Array.isArray(verbatim)) {
            const newVerbatim = [...verbatim];
            if (verbatim.some(content => content.type === 'text')) {
                newVerbatim.forEach((element) => {
                    if (element.type === 'text') element.content = event.target.value;  // Update the content of the text element
                });
            } else {
                newVerbatim = [{ type: 'text', content: event.target.value }, ...newVerbatim]  // Add a new text element if none exists
            }
            setVerbatim(newVerbatim);  // Update state with the new verbatim array
        } else {
            setVerbatim([{ type: 'text', content: event.target.value }]);  // Update state with the new verbatim value
        }
    };  // Update state with the new verbatim value

    // Handle changes to the link input field
    const handleLinkChange = (event) => setLink(event.target.value);  // Update state with the new link value

    const updateSourceFromLink = () => {
        setLinkError('');
        if (link !== '') {
            try {
                const { hostname, hostnameWithoutTld, baseName, baseNameFormatted } = extractDomainInfo(link?.trim());
                
                if (hostname && hostnameWithoutTld && baseName && baseNameFormatted) {
                    let sourceName = "" // Default to the base name if no keyword matches
                    // Check the hostname against the source categories to determine the source
                    labels['Sources']?.forEach((source) => {
                        if (source.keywords.includes(hostnameWithoutTld)) {
                            sourceName = source.name;  // Set the source name if a keyword matches
                        }
                    })

                    if (sourceName === "") sourceName = baseNameFormatted // Capitalize the base name if no keyword matches
                    // Set the active labels with the source information
                    if (!sourceOptions.includes(sourceName) && !["Blog", "Forum", "Review"].includes(sourceName)) setSourceOptions(prev => [...prev, sourceName]); // Add the source to the options if not already present
                        setActiveLabels((prevLabels = {}) => {
                        return {
                            ...prevLabels,
                            Sources: [
                                { name: sourceName, state: 'active', type: 'Sources' }
                            ]
                        };
                    });
                } else {
                    setSourceOptions(labels['Sources']?.map(source => source.name) || ["Blog", "Forum", "Review"]); // Reset to default options if no link is provided
                    // Delete the "Sources" only if the link is not valid
                    setActiveLabels((prevLabels = {}) => {
                        const { Sources, ...rest } = prevLabels;
                        return rest;
                    });
                    setLinkError('Please provide a valid link, for example: www.example.com...');
                }
            } catch (error) {
                console.error("Erreur lors de l'extraction du nom de domaine :", error);
            }
        } else {
            setSourceOptions(labels['Sources']?.map(source => source.name) || ["Blog", "Forum", "Review"]); // Reset to default options if no link is provided
            // // Delete the "Sources" only if the link is not valid
            setActiveLabels((prevLabels = {}) => {
                const { Sources, ...rest } = prevLabels;
                return rest;
            });
            setLinkError('Please provide a valid link, for example: www.example.com...');
        }
    };

    // Handle click on a gender button
    const handleGenderClick = (gender) => {
        setActiveLabels((prevLabels = {}) => {
            const currentGenderLabels = Array.isArray(prevLabels['Gender']) ? prevLabels['Gender'] : [];

            const isSelected = currentGenderLabels.some(label => label?.name === gender);

            const updatedGenderLabels = isSelected
                ? [] // Deselect if already selected
                : [{ name: gender, state: 'active', type: 'Gender' }];

            return { ...prevLabels, Gender: updatedGenderLabels };
        });
    };

    // Handle click on an age button
    const handleAgeClick = (age) => {
        setActiveLabels((prevLabels = {}) => {
            const currentAgeLabels = Array.isArray(prevLabels['Age']) ? prevLabels['Age'] : [];

            // Check if the age is already selected
            const isSelected = currentAgeLabels.some(label => label?.name === age);

            // If selected, remove it; otherwise, add it as the only active age (single selection for Age)
            const updatedAgeLabels = isSelected
                ? [] // Deselect all ages if clicking an already selected one
                : [{ name: age, state: 'active', type: 'Age' }];

            return { ...prevLabels, Age: updatedAgeLabels };
        });
    };
    // Handle click on an source button
    const handleSourceClick = (source) => {
        setActiveLabels((prevLabels = {}) => {
            const currentSourceLabels = Array.isArray(prevLabels['Sources']) ? prevLabels['Sources'] : [];

            // Check if the age is already selected
            const isSelected = currentSourceLabels.some(label => label?.name === source);

            // If selected, remove it; otherwise, add it as the only active source (single selection for Age)
            const updatedSourceLabels = isSelected
                ? [] // Deselect all sources if clicking an already selected one
                : [{ name: source, state: 'active', type: 'Sources' }];

            return { ...prevLabels, Sources: updatedSourceLabels };
        });
    };

    // Handle click on a sentiment button
    const handleSentimentClick = (sentiment) => {
        setActiveLabels((prevLabels = {}) => {
            const currentSentimentLabels = Array.isArray(prevLabels['Sentiment']) ? prevLabels['Sentiment'] : [];

            const isSelected = currentSentimentLabels.some(label => label?.name === sentiment);

            const updatedSentimentLabels = isSelected
                ? [] // Deselect if already selected
                : [{ name: sentiment, state: sentiment, type: 'Sentiment' }];

            return { ...prevLabels, Sentiment: updatedSentimentLabels };
        });
    };

    // Function to handle clicks on non-special labels (i.e., not Gender, Age, or Sentiment)
    const handleLabelClick = (name, type) => {
        // Skip handling for Gender, Age, and Sentiment labels as they have their own logic
        if (['Gender', 'Age', 'Sentiment', 'Sources'].includes(type)) {
            return;  // Exit early if one of the special labels is clicked
        }

        // Check if the clicked label type supports sentiment (using labelTypes)
        if (labelTypes[type]?.sentimentEnabled) {
            cycleLabelState(name, true, type);  // Cycle sentiment states if sentiment is enabled for this label type
        } else {
            // For non-sentiment labels, toggle between active and inactive states
            setActiveLabels((prevLabels) => {
                const labelGroup = prevLabels[type] || [];  // Get existing labels for this type or initialize as empty
                const labelExists = labelGroup.find((l) => l.name === name);  // Check if the clicked label is already active

                if (labelExists) {
                    // If the label is already active, remove it from the group
                    const updatedLabelGroup = labelGroup.filter((l) => l.name !== name);
                    return updatedLabelGroup.length
                        ? { ...prevLabels, [type]: updatedLabelGroup }  // Update state with the remaining labels
                        : { ...prevLabels, [type]: undefined };  // If no labels remain, remove the type from active labels
                } else {
                    // Check if multiple values are allowed for this label type
                    const isMultiValueAllowed = labelTypes[type]?.multiValue !== false;
                    const newLabel = { name, state: 'active', type };  // Create a new label object with state 'active'
                    // If multiple values are not allowed and there's already an active label, replace it else add the new label
                    if (!isMultiValueAllowed && labelGroup.length > 0) {
                        return { ...prevLabels, [type]: [newLabel] }; // Replace existing label with the new one
                    } else {
                        return { ...prevLabels, [type]: [...labelGroup, newLabel] }; // Add the new label to the group
                    }
                }
            });
        }
    };

    /************************************************
  * UTILITY FUNCTIONS
  ************************************************/

    // Function to check if a specific label value is selected for a given label type (like 'Gender', 'Age', etc.)
    const updateSelectedClass = (labelType, labelValue) => {
        const activeLabelArray = activeLabels[labelType];  // Get the array of active labels for the given label type
        if (Array.isArray(activeLabelArray) && activeLabelArray.length > 0) {
            // Return 'selected' if the labelValue exists in the active labels, otherwise return an empty string
            return activeLabelArray.some(labelObj => labelObj.name === labelValue) ? 'selected' : '';
        }
        return '';  // Return empty if no active labels found for the type
    };

    const sentimentStates = ['inactive', 'positive', 'negative', 'mixed'];  // Define possible sentiment states for cycling

    // Function to cycle through label states, handling sentiment labels if enabled
    const cycleLabelState = (labelName, isSentimentEnabled, labelType) => {
        setActiveLabels((prevLabels) => {
            const labelGroup = prevLabels[labelType] || [];  // Get existing labels for the label type or initialize as empty
            const labelObj = labelGroup.find((l) => l.name === labelName);  // Find the specific label object by name
            const currentState = labelObj?.state || 'inactive';  // Set the current state, default to 'inactive' if not found

            let updatedLabelGroup;  // Declare variable to hold the updated label group
            let newState = "inactive";  // Initialize newState as 'inactive'

            if (isSentimentEnabled) {
                // If sentiment is enabled, cycle through the predefined sentiment states
                const currentIndex = sentimentStates.indexOf(currentState);  // Find the current state's index
                const nextIndex = (currentIndex + 1) % sentimentStates.length;  // Get the next state in the cycle
                newState = sentimentStates[nextIndex];  // Set newState to the next sentiment state

                if (newState === "inactive") {
                    // If the new state is 'inactive', remove the label from the active group
                    updatedLabelGroup = labelGroup.filter((l) => l.name !== labelName);
                } else {
                    // Check if multiple values are allowed for this label type
                    const isMultiValueAllowed = labelTypes[labelType]?.multiValue !== false;
                    // If the label should remain active, update its state or add it if it's new
                    if (labelObj) {
                        updatedLabelGroup = labelGroup.map((l) =>
                            l.name === labelName ? { ...l, state: newState } : l  // Update state for the existing label
                        );
                    } else {
                        // The label does not exist yet
                        const newLabel = { name: labelName, state: newState, type: labelType };
                        // If multiple values are not allowed and there's already an active label, replace it else add the new label
                        if (!isMultiValueAllowed && labelGroup.length > 0) {
                            updatedLabelGroup = [newLabel];
                        } else {
                            updatedLabelGroup = [...labelGroup, newLabel];
                        }
                    }
                }
            }

            // Immediately update the styling of the button representing this label
            const button = document.querySelector(`button[data-name="${labelName}"][data-type="${labelType}"]`);
            if (button) {
                updateLabelButtonState(button, newState);  // Call function to update the button's visual state
            }

            // Return the updated state with the modified label group for the given label type
            return {
                ...prevLabels,
                [labelType]: updatedLabelGroup,
            };
        });
    };

    // Function to handle the addition of manual labels
    const addManualLabel = (label) => {
        // Prevent adding predefined label types manually (e.g., Gender, Age, Sentiment)
        if (['Gender', 'Age', 'Sentiment', 'Sources'].includes(activeTab)) {
            alert('You cannot manually add Gender, Age, or Sentiment labels.');  // Show an alert if user tries to add these
            return;  // Exit the function if these label types are attempted
        }

        // Get the labels for the active tab (current label type)
        const currentLabelType = labels[activeTab];

        if (currentLabelType) {
            // Check if the label is already active or does not exist in the current label type
            if (!activeLabels[activeTab]?.find(l => l.name === label) && currentLabelType.find(lbl => lbl.name === label)) {
                let newState;
                // If the label type has sentiment enabled, default the state to 'positive'
                if (labelTypes[activeTab]?.sentimentEnabled) {
                    newState = 'positive';  // Default sentiment-enabled labels to 'positive'
                } else {
                    newState = 'active';  // For other labels, set the state to 'active'
                }

                const newLabel = { name: label, state: newState };  // Create a new label object with the default state
                // Check if multiple values are allowed for this label type
                const isMultiValueAllowed = labelTypes[activeTab]?.multiValue !== false;
                // Update the activeLabels state to include the new label
                setActiveLabels((prevLabels) => {
                    const labelGroup = prevLabels[activeTab] || [];
                    // if multiple values are not allowed and there's already an active label, replace it else if add the new label else create new array
                    if (!isMultiValueAllowed && labelGroup.length > 0) {
                        return {
                            ...prevLabels,
                            [activeTab]: [newLabel] // Replace existing label with the new one
                        };
                    } else if (prevLabels[activeTab]) {
                        return {
                            ...prevLabels,
                            [activeTab]: [...prevLabels[activeTab], newLabel],  // Append the new label to the current label array
                        };
                    } else {
                        return { ...prevLabels, [activeTab]: [newLabel] };  // Create a new array if none exists for the label type
                    }
                });

                // Re-run the function to update the displayed labels in the "Additional Labels" section
                updateLabelingPageLabels();  // Ensure the new label is displayed
            } else if (activeLabels[activeTab]?.find(l => l.name === label)) {
                // If the label is already active, show an alert
                alert('Label is already active.');
            } else {
                // If the label doesn't exist in the list, show an alert
                alert('Label does not exist in the label list.');
            }
        } else {
            // If no labels are found for the current tab, show an alert
            alert('No labels found for the current label type.');
        }
    };

    const handleQuickAddLabel = async (label, tab) => {
        const success = await addLabel(label, tab); // Assume addLabel returns a success boolean or promise
    
        if (success) {
            // Reset the input field
            const inputElement = document.getElementById('quick-label-add');
            if (inputElement) {
                inputElement.value = ''; // Clear the input field
            }
    
            // Set a success status message
            setStatusMessage(`The label "${label}" was successfully added with type "${tab}".`);
        
            addManualLabel(label); // Execute only if successful
        } else {
            // Handle failure case (optional)
            setStatusMessage(`Failed to add the label "${label}". Please try again.`);
        }
    }

    // Function to suggest labels based on the current verbatim text
    const suggestLabelsFromVerbatim = (text) => {
        const foundLabels = [];  // Array to store any labels found in the text

        // Iterate over each label type (e.g., Gender, Age, Sentiment)
        Object.keys(labels).forEach((labelType) => {
            const labelArray = labels[labelType];  // Get the array of labels for this label type

            // Safeguard: Ensure labelArray is an array before iterating
            if (Array.isArray(labelArray)) {
                // Loop through each label in the label type
                labelArray.forEach((labelObj) => {
                    // Check if the label has associated keywords, then match those against the verbatim text
                    if (Array.isArray(labelObj.keywords) && labelObj.keywords.length > 0) {
                        labelObj.keywords.forEach(keywordString => {
                        const individualKeywords = keywordString.split(/[;,]/).map(kw => kw.trim());  // Split by ';' or ',' and trim spaces
                        // Check if any individual keyword matches the verbatim text
                        if (
                            Array.isArray(individualKeywords) &&
                            individualKeywords.some(keyword => text?.toLowerCase().includes(keyword?.toLowerCase()))
                        ) {
                            if (labelObj?.name && labelType) {
                                foundLabels.push({ name: labelObj.name, type: labelType });  // Add matching labels to foundLabels
                            }
                        }
                        });
                    // If attribute label has no keywords, add it to the found labels
                    } else if (Array.isArray(labelObj.keywords) && labelObj.keywords.length === 0) {
                        if (labelObj?.name && labelType) {
                            foundLabels.push({ name: labelObj.name, type: labelType });
                        }
                    }
                });
            } else {
                console.warn(`Expected array but got:`, labelArray);  // Warn if labelArray is not an array
            }
        });

        // Set the found labels as suggested labels for the relevant tabs
        setSuggestedLabels(foundLabels);

        // Update the labels displayed in the "Additional Labels" area
        updateLabelingPageLabels();
    };

      // Add a function to handle image uploads
    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            setStatusMessage('Image optimization in progress...');
            // Use imageToBase64 to optimize and compress the image
            // this function will resize and compress the image before converting it to Base64
            const base64Image = await imageToBase64(file);
            
            // Add the image to the verbatim content
            setVerbatim(prev => {
                if (typeof prev === 'string') {
                    return [{ type: 'text', content: prev }, { type: 'image', content: base64Image }];
                } else if (Array.isArray(prev)) {
                    const existingImageIndex = prev.findIndex(item => item.type === 'image');          
                    // if an image already exists, replace it
                    if (existingImageIndex !== -1) {
                        // create a new array to avoid mutating the state directly
                        const newContent = [...prev];
                        // replace the existing image with the new one
                        newContent[existingImageIndex] = { type: 'image', content: base64Image };
                        return newContent;
                    } else {
                        // else, add the new image to the end of the array
                        return [...prev, { type: 'image', content: base64Image }];
                    }
                }
            });
            setStatusMessage('Image pasted successfully!');
        } catch (error) {
            console.error('Error while processing the pasted image', error);
            setStatusMessage('Error while processing the image. Please try again.');
        }
    };

    // Add a new verbatim input field
    const handlePaste = async (event, index) => {
        event.preventDefault();
        const clipboardData = event.clipboardData;
        const items = clipboardData.items;
        
        // checker si le presse-papiers contient des données
        if (!items) return;
        
        let hasHandledItem = false;
        
        for (let i = 0; i < items.length; i++) {
          // Check if the item is an image
          if (items[i].type.indexOf('image') !== -1) {
            hasHandledItem = true;
            setStatusMessage('Processing pasted image...');
            
            try {
              const file = items[i].getAsFile();
              if (file) {
                const base64Image = await imageToBase64(file);
    
                setVerbatim(prev => {
                  if (typeof prev === 'string') {
                    return [{ type: 'text', content: prev }, { type: 'image', content: base64Image }];
                  } else if (Array.isArray(prev)) {
                    const existingImageIndex = prev.findIndex(item => item.type === 'image'); 
    
                    if (existingImageIndex !== -1) {
                      const newContent = [...prev];
                      newContent[existingImageIndex] = { type: 'image', content: base64Image };
    
                      return newContent;
                    } else {
                      return [...prev, { type: 'image', content: base64Image }];
                    }
                  }
                });
                setStatusMessage('Image pasted successfully!');
              }
            } catch (error) {
              console.error('Error while processing the pasted image', error);
              setStatusMessage('Error while processing the image. Please try again.');
            }
            break;
          }
        }
        
        // if no image was handled, check for text
        if (!hasHandledItem) {
          const text = clipboardData.getData('text');
          if (text) {
            // Update textarea content with pasted text
            const newContent = [...verbatim];
            const startPos = event.target.selectionStart;
            const endPos = event.target.selectionEnd;
            const currentContent = newContent[index].content;
            
            // Insert the pasted text at the cursor position
            newContent[index] = { 
              ...newContent[index], 
              content: currentContent.substring(0, startPos) + text + currentContent.substring(endPos) 
            };
            
            setVerbatim(newContent);
          }
        }
    };

    // Fuction to delete an image from the verbatim content
    const removeImage = (index) => {
        const newContent = [...verbatim];
        newContent.splice(index, 1);
        setVerbatim(newContent);
    };

    // Handle deleting a specific verbatim by ID
    const handleDeleteVerbatim = (id) => {
        if (window.confirm("Are you sure you want to delete this verbatim?")) {
        // Ask for confirmation before deleting
            deleteVerbatim(id); // Call the delete function passed as a prop
            setVerbatim('');
            setLink('');
            setActiveLabels({});
            setSuggestedLabels([]);
            setEditingVerbatimId(null);
            setStatusMessage("Verbatim successfully deleted");
            if (currentVerbatimIndex + 1 < verbatimsToEdit.length) {
                setCurrentVerbatimIndex(currentVerbatimIndex + 1); // Passer au verbatim suivant
            } else {
                // Si tous les verbatims ont été édités, terminer le processus
                alert('Batch editing completed!');
                setVerbatimsToEdit([]); // Vider la liste
                setCurrentVerbatimIndex(0); // Réinitialiser l'index
            }
        }
    };

    return (
        <div className="labeling-container">
            <div className="review-input">
                {verbatimsToEdit.length > 0 ? (
                    <h2>Verbatim {currentVerbatimIndex + 1} of {verbatimsToEdit.length} remaining.</h2>
                ): <h2>No verbatim to be modified</h2>}
                <h4><label htmlFor="review-text">Verbatim Text*:</label></h4>
                <div className="verbatim-content-block">
                    <textarea
                        id="review-text"
                        placeholder="Enter the beauty product review here..."
                        value={typeof verbatim === 'string' ? verbatim : verbatim[0]?.content}
                        onChange={handleVerbatimChange}
                        onPaste={(e) => handlePaste(e, 0)} // Call the handlePaste function on paste event
                        autoCorrect="off"
                        spellCheck="false"
                        style={{ height: Array.isArray(verbatim) && verbatim.some(content => content.type === 'image') ? '150px' : '300px' }}
                    />
                </div>
                {Array.isArray(verbatim) && verbatim.some(content => content.type === 'image') &&
                    <div className="verbatim-content-block">
                        <div className="verbatim-image-container" style={{ marginRight: '0px'}}>
                            <img 
                                src={base64ToImage(verbatim[1].content)} 
                                alt="Verbatim image" 
                                className="verbatim-image"
                                onError={(e) => {
                                    console.error("Image loading error", e);
                                    e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiM5OTkiPkltYWdlIEVycm9yPC90ZXh0Pjwvc3ZnPg==";
                                }}
                            />
                            <span className="remove-image-icon" onClick={() => removeImage(verbatim.indexOf(verbatim[1]))}>
                                <i className="fa-solid fa-times"></i>
                            </span>
                        </div>
                    </div>
                }
                <div className="verbatim-controls">
                    <button className="styled-button" style={{ fontSize: "0.9em", padding: "7px" }} onClick={() => imageInputRef.current.click()}>
                    <i className="fa-solid fa-image"></i> Add an image
                    </button>
                    
                    <input
                    type="file"
                    ref={imageInputRef}
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleImageUpload}
                    />
                </div>
                <div className="link-input">
                    <h4>
                        <label htmlFor="verbatim-link">Verbatim Link*:</label> 
                        {link && <a href={link} target="_blank" rel="noopener noreferrer"><i className="fa-solid fa-link"></i></a>}
                    </h4>
                    <div className="input-with-icon">
                        <input
                            type="text"
                            id="verbatim-link"
                            placeholder="Enter the link to the verbatim"
                            value={link}
                            onChange={handleLinkChange}
                            onBlur={updateSourceFromLink}
                        />
                    </div>
                    {linkError && <small className="transcript-error-message">{linkError}</small>}
                </div>
                <div>
                    <h5>*Mandatory: a value must be entered.</h5>
                </div>
            </div>

            <div className="label-selection">
                <div className="flex-container label-area-header">
                    <h2>Select relevant labels below</h2>
                    <div className="flex-container" style={{ flexBasis: '45%', flexDirection: 'row-reverse' }}>
                        <button className="styled-button" style={{ flexBasis: '50%' }} onClick={() => {
                            storeVerbatim();        // Call the main storage function
                            {/*storeVerbatimBackup()*/ };  // Call the backup storage function
                        }}>
                            <i className="fa-solid fa-floppy-disk"></i> Store & Next
                        </button>
                        {editingVerbatimId && 
                            <button className="styled-button" style={{ flexBasis: '14%', backgroundColor: '#c82333' }} onClick={() => handleDeleteVerbatim(editingVerbatimId)}>
                                <i className="fa fa-trash"></i>
                            </button>
                        }
                    </div>
                </div>
                <div className="demographics-row">
                    <div className="gender-column">
                        <h4>Gender*</h4>
                        <div id="gender-labels">
                            {genderOptions.map((gender) => (
                                <button
                                    key={gender}
                                    className={`label-button gender-button ${Array.isArray(activeLabels?.['Gender']) && activeLabels['Gender'].some(label => label?.name === gender)
                                        ? `selected gender-${gender?.toLowerCase().replace(/\s+/g, '-')}`
                                        : ''
                                        }`}
                                    onClick={() => handleGenderClick(gender)}
                                >
                                    {gender}
                                </button>

                            ))}

                        </div>
                    </div>
                    <div className="age-column">
                        <h4>Age*</h4>
                        <div id="age-labels">
                            {ageOptions.map((age) => (
                                <button
                                    key={age}
                                    className={`label-button age-button ${Array.isArray(activeLabels?.['Age']) && activeLabels['Age'].some(label => label?.name === age)
                                        ? `selected age-${age?.toLowerCase().replace(/[^a-z0-9]/g, '-')}`
                                        : ''
                                        }`}
                                    onClick={() => handleAgeClick(age)}
                                >
                                    {age}
                                </button>

                            ))}

                        </div>
                    </div>
                </div>
                {/* <div className="label-section">
                    <h4>Sentiment*</h4>
                    <div id="sentiment-labels">
                        {sentiments.map((sentiment) => (
                            <button
                                key={sentiment}
                                className={`label-button sentiment-button ${Array.isArray(activeLabels?.['Sentiment']) && activeLabels['Sentiment'].some(label => label?.name === sentiment)
                                    ? `selected ${sentiment?.toLowerCase()}`
                                    : ''
                                    }`}
                                onClick={() => handleSentimentClick(sentiment)}
                            >
                                {sentiment}
                            </button>
                        ))}
                    </div>
                </div> */}
                <div className="demographics-row">
                    <div className="label-section gender-column">
                        <h4>Sentiment*</h4>
                        <div id="sentiment-labels">
                            {sentiments.map((sentiment) => (
                                <button
                                    key={sentiment}
                                    className={`label-button sentiment-button ${Array.isArray(activeLabels?.['Sentiment']) && activeLabels['Sentiment'].some(label => label?.name === sentiment)
                                        ? `selected ${sentiment?.toLowerCase()}`
                                        : ''
                                        }`}
                                    onClick={() => handleSentimentClick(sentiment)}
                                >
                                    {sentiment}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="age-column">
                        <h4>Sources*</h4>
                        <div id="source-labels">
                        {sourceOptions.map((source) => {
                            let sourceName = ["Blog", "Forum", "Review"].includes(source) ? source.toLowerCase().replace(/[^a-z0-9]/g, '-') : 'social-media';
                            const isSelected =
                                Array.isArray(activeLabels?.["Sources"]) &&
                                activeLabels["Sources"].some((label) => label?.name === source);

                            return <button
                                key={source}
                                ref ={isSelected ? selectedRef : null}
                                className={`label-button source-button ${isSelected ? `selected source-${sourceName}`: ''}`}
                                onClick={() => handleSourceClick(source)}
                            >
                                {source}
                            </button>
                        })}
                        </div>
                    </div>
                </div>
                        <div className="label-section">
                    <h4>Additional Labels</h4>
                    <div id="tabs-container-wrapper">
                        <div id="label-tabs" className="tabs-container">
                            {Object.keys(labels)
                                .filter(type => !['Gender', 'Age', 'Sentiment', 'Sources'].includes(type)) // Exclude specific label types
                                .map((tab) => {
                                    const isMandatory = labelTypes[tab]?.mandatory;
                                    const isSentimentEnabled = labelTypes[tab]?.sentimentEnabled;
                                    const isMultiValue = labelTypes[tab]?.multiValue === false; // Check if multiValues is false

                                    return (
                                        <button
                                            key={tab}
                                            className={`label-tab ${activeTab === tab ? 'active' : ''}`}
                                            onClick={() => setActiveTab(tab)}
                                        >
                                            {tab}
                                            {isMandatory && <i className="fa-solid fa-exclamation-triangle tab-icon" title="Mandatory"></i>}
                                            {isSentimentEnabled && <i className="fa-solid fa-smile tab-icon" title="Sentiment Enabled"></i>}
                                            {isMultiValue && <i className="fa-solid fa-tag tab-icon" title="Single Value Only"></i>}
                                            {!isMultiValue && <i className="fa-solid fa-tags tab-icon" title="Multiple Values Possible"></i>}
                                        </button>
                                    );
                                })}
                        </div>
                        <div id="label-labels">
                            {activeTab && (
                                <div className="labels-list">
                                    {labels[activeTab]?.map((labelObj) => {
                                        const { name, state } = labelObj;  // Extract label name and state
                                        const stateClass = state || 'inactive'; // Default to 'inactive' if state is not set

                                        return (
                                            <button
                                                key={name}
                                                className={`label-button ${stateClass}`}  // Apply the correct class dynamically
                                                onClick={() => handleLabelClick(name)}
                                            >
                                                {name}
                                                {labelTypes[activeTab]?.sentimentEnabled && (
                                                    <i className="fa-solid fa-smile sentiment-icon" title="Sentiment Enabled"></i>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        <div id="manual-label-add">
                            <div id="label-input">
                                <input type="text" id="manual-label-input" placeholder="Search a label" />
                                <div id="label-suggestions"></div>
                            </div>
                            <div id="label-quick-add">
                                <input
                                    type="text"
                                    id="quick-label-add"
                                    placeholder="Add a label immediately in your setup"
                                />
                                <MinimalistButton
                                    onClick={() => {
                                        const inputValue = document.getElementById('quick-label-add').value;
                                        handleQuickAddLabel(inputValue, activeTab);
                                    }}
                                >
                                    +
                                </MinimalistButton>
                            </div>
                        </div>
                    </div>
                </div>
                <div id="selected-labels">
                    <h4>Active Labels:</h4>
                    <div id="selected-labels-list" className="flex-container"></div>
                    <br />
                </div>
                <div>
                    <h5>*Mandatory: a value must be selected.</h5>
                </div>
            </div>
            <StatusBar message={statusMessage} />
        </div>
    );
};

export default BatchLabelingPage;